import { useRef, useState, useLayoutEffect, useCallback } from "react";
import cn from "mcn";
import { useGridSettingsWidth } from "../../grid-settings/hooks";
import { CloseIcon } from "../../icons/CloseIcon";
import { Button } from "../../ui";
import { getVisibleBottomBarRect } from "../../layout/visibleBottomBar";

const BOTTOM_MARGIN = 16;

const getBottomBarHeight = (): number => getVisibleBottomBarRect()?.height ?? 0;

export const TracePrecedentsModalWrapper = ({
  children,
  constrainRef,
  onClose,
}: {
  children: React.ReactNode;
  constrainRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
}) => {
  "use no memo";

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new MutationObserver(() => {
      el.scrollTop = el.scrollHeight;
    });
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const updateMaxHeight = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    const contentTop = content.getBoundingClientRect().top;
    const available = Math.max(100, window.innerHeight - contentTop - getBottomBarHeight() - BOTTOM_MARGIN);
    content.style.maxHeight = `${available}px`;
  }, []);

  const {
    dragging,
    ref: dragRef,
    start: dragStart,
  } = useDraggable({
    constrainRef,
    onMove: updateMaxHeight,
  });
  const left = useGridSettingsWidth();

  useLayoutEffect(() => {
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [updateMaxHeight]);

  return (
    <div
      ref={dragRef}
      data-testid="modal"
      className={cn(
        "rounded-lg border outline-outline overflow-hidden select-none",
        "z-[30] bg-card absolute grid grid-rows-[min-content_1fr] max-w-[90vw] max-h-full py-4",
        "transition-[opacity,box-shadow] duration-100 [&_input]:select-text [&_textarea]:select-text opacity-0",
        [dragging, "opacity-90"],
        [
          dragging,
          "dark:shadow-[5px_3px_7px_1px_rgba(100,100,100,0.25)] shadow-[5px_3px_7px_1px_rgba(0,0,0,0.25)]",
          "dark:shadow-[4px_2px_6px_-1px_rgba(100,100,100,0.25)] shadow-[4px_2px_6px_-1px_rgba(0,0,0,0.25)]",
        ],
      )}
      style={{ transform: `translateX(${left}px)` }}
    >
      <div
        className={cn(
          "flex w-full text-foreground items-center px-6 py-3",
          dragging ? "cursor-grabbing" : "cursor-grab",
        )}
        onMouseDown={dragStart}
        onTouchStart={dragStart}
      >
        <span className="flex-1 text-xl font-bold">Trace Precedents</span>
        <CloseIcon onClick={onClose} onMouseDown={(e) => e.stopPropagation()} />
      </div>
      <div ref={contentRef} className="flex flex-col min-h-0 min-w-0">
        <p className="px-6 pt-1 pb-4 shrink-0 w-0 min-w-full text-base text-foreground-muted">
          Click on a formula block to view precedents. Right click on any Curve block to add that Curve to your Page.
        </p>
        <div ref={scrollRef} className="overflow-y-auto flex-1 min-h-0 min-w-0">
          {children}
        </div>
        <div className="px-6 pt-4 flex justify-end shrink-0">
          <Button buttonType="closePrimary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const useDraggable = ({
  constrainRef,
  onMove: onMoveProp,
}: {
  constrainRef: React.RefObject<HTMLDivElement>;
  onMove?: () => void;
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set position only — no reveal logic.
  const applyPosition = useCallback(
    (x: number, y: number) => {
      const bounding = constrainRef.current?.getBoundingClientRect();
      if (elementRef.current !== null) {
        elementRef.current.style.left = `${(bounding?.left ?? 0) + x}px`;
        elementRef.current.style.top = `${(bounding?.top ?? 0) + y}px`;
      }
      onMoveProp?.();
    },
    [constrainRef, onMoveProp],
  );

  const update = useCallback(
    (x: number, y: number) => {
      offsetRef.current = { x, y };
      applyPosition(x, y);
    },
    [applyPosition],
  );

  // Reveal the modal with no animation after sizing has settled (debounced).
  const scheduleReveal = useCallback(() => {
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => {
      const el = elementRef.current;
      if (!el || el.style.opacity === "1") return;
      el.style.transition = "none";
      el.style.opacity = "1";
      void el.offsetHeight;
      el.style.transition = "";
    }, 80);
  }, []);

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    hasDraggedRef.current = true;
    setDragging(true);
    // If the user drags before the reveal debounce fires, reveal immediately.
    const el = elementRef.current;
    if (el && el.style.opacity !== "1") {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
      el.style.transition = "none";
      el.style.opacity = "1";
      void el.offsetHeight;
      el.style.transition = "";
    }
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    const parentRect = constrainRef.current?.getBoundingClientRect();
    if (!parentRect) return;
    stateRef.current = {
      x: clientX - offsetRef.current.x - parentRect.left,
      y: clientY - offsetRef.current.y - parentRect.top,
    };
  };

  useLayoutEffect(() => {
    if (!dragging) return;

    const onMove = (e: MouseEvent | TouchEvent) => {
      const pos =
        "touches" in e ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };

      if (elementRef.current !== null) {
        const parentRect = constrainRef.current?.getBoundingClientRect();
        const modalRect = elementRef.current.getBoundingClientRect();
        if (!parentRect || !modalRect) return;

        let newX = pos.x - stateRef.current.x - parentRect.left;
        let newY = pos.y - stateRef.current.y - parentRect.top;

        newX = Math.max(0, Math.min(newX, parentRect.width - modalRect.width));
        newY = Math.max(0, Math.min(newY, parentRect.height - modalRect.height));

        update(newX, newY);
      }
    };

    const onUp = () => setDragging(false);

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, update, constrainRef]);

  // Parent resize: clamp position to keep the modal in bounds.
  useLayoutEffect(() => {
    const parent = constrainRef.current;
    if (!parent) return;
    const observer = new ResizeObserver(() => {
      if (elementRef.current === null) return;
      const parentRect = parent.getBoundingClientRect();
      const modalRect = elementRef.current.getBoundingClientRect();
      const { x, y } = offsetRef.current;
      const nx = Math.max(0, Math.min(x, parentRect.width - modalRect.width));
      const ny = Math.max(0, Math.min(y, parentRect.height - modalRect.height));
      update(nx, ny);
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, [constrainRef, update]);

  // Modal resize: re-center (before first drag) or clamp (after drag), then
  // schedule reveal so the modal only appears once its size has settled.
  useLayoutEffect(() => {
    const modal = elementRef.current;
    if (!modal) return;
    const observer = new ResizeObserver(() => {
      const parent = constrainRef.current;
      if (!parent || !elementRef.current) return;
      const parentRect = parent.getBoundingClientRect();
      const modalRect = elementRef.current.getBoundingClientRect();
      if (hasDraggedRef.current) {
        const { x, y } = offsetRef.current;
        const nx = Math.max(0, Math.min(x, parentRect.width - modalRect.width));
        const ny = Math.max(0, Math.min(y, parentRect.height - modalRect.height));
        update(nx, ny);
      } else {
        const nx = (parentRect.width - modalRect.width) / 2;
        const ny = (parentRect.height - modalRect.height) / 2;
        update(nx, ny);
        scheduleReveal();
      }
    });
    observer.observe(modal);
    return () => {
      observer.disconnect();
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    };
  }, [constrainRef, update, scheduleReveal]);

  return { dragging, ref: elementRef, start };
};
